import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type CSSProperties, type KeyboardEvent } from 'react';
import {
  DeploymentKind,
  formatRobotShortId,
  formatLoaderSerial,
  LOADER_STATUS_LABELS,
  ROBOT_LOCATION_LABELS,
  ROBOT_STATUS_LABELS,
  RobotStatus,
  UserRole,
  type RobotSummaryDto,
  type RobotTimelineEntry,
  type RobotTimelineResponse,
} from '@bhops/shared';
import { robotsApi } from '@/api/robots';
import { deploymentsApi } from '@/api/deployments';
import { SpareBadge } from '@/components/SpareBadge';
import { assignmentsApi } from '@/api/assignments';
import { apiClient, extractApiError } from '@/api/client';
import { canCorrectRobotStatus, canDeleteRobot, canDeployRobot, canEditMfg } from '@/permissions';
import { ROBOT_STATUS_TINT, VerificationPill } from '@/components/StatusPill';
import { robotQrsApi } from '@/api/robot-qrs';
import { RowMoreMenu, type RowMoreItem } from './customers/RowMoreMenu';
import { Button } from '@/components/Form';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Modal } from '@/components/Modal';
import { FilterChip } from '@/components/FilterChip';
import { useToast } from '@/components/Toast';
import { useAuthStore } from '@/stores/auth';
import { RobotEditModal } from './robots/RobotEditModal';
import { RobotStatusCorrectionModal } from './robots/RobotStatusCorrectionModal';
import { RobotContractKindModal } from './robots/RobotContractKindModal';
import { RobotContractLinkModal, RobotContractUnlinkModal } from './robots/RobotContractLinkModal';
import { isOtherEntityContract, OTHER_ENTITY_CONTRACT_HINT } from './robots/robot-entity';
// 구분 수동 정정(plan 2-5) — '구분' 표기는 정본 두 단어 + 빈 값 표기만 쓴다
// (RobotsListPage 의 관례와 동일).
import {
  CONTRACT_KIND_SALE,
  CONTRACT_KIND_SUBSCRIPTION,
  CONTRACT_REASON_EMPTY,
} from '@/lib/contract-reason';
import { DeployRobotModal } from './robots/DeployRobotModal';
import { ReturnRobotModal } from './robots/ReturnRobotModal';
import { ServiceOrderCreateModal } from './ServiceOrderCreateModal';
import { QRTab } from './robots/QRTab';
import {
  DeploymentsTab,
  ManufactureTab,
  FieldServiceTab,
  FirmwareTab,
  TraceTab,
} from './robots/RobotDetailTabs';
// v0.4.0 §6.4 — 부품 탭. (솔루션 탭은 S2 §9.3/Phase 2.8에서 제거 — 솔루션은
// 로봇이 아닌 매장 단위로 이동(StoreDetailPage 솔루션 섹션).)
import { RobotPartsTab } from './robots/RobotPartsTab';
import { LoaderTab } from './robots/LoaderTab';
import { robotLoaderApi } from '@/api/loaders';
import { LoaderConfigCode } from '@/components/LoaderConfigCode';
import { Icon } from '@/components/Icon';
import { robotStatusToStage } from '@/components/StatusBar';
import {
  Empty,
  Fact,
  FactList,
  FactSection,
  HistoryList,
  LifecycleTrack,
  Sheet,
  SheetEmpty,
  SheetHead,
} from './robots/robot-detail-ui';
import { kstDateTime, kstYmd } from '@/lib/format-date';

// docs/work_log v1209 — Date 안전 포매팅은 lib/format-date.ts 사용.
// 추가수정 2차(26-07-27) §2 — 표시 날짜·시각은 KST 정본(kstYmd/kstDateTime).
// 이전 버전의 local fmtDateOnly 헬퍼는 제거됨.

// Plan §D-4 (PDF p.9 / p.12 / p.21) — 8-tab structure:
//   개요 / 출고 이력 / 계약 / 제조 이력 / 고장 이력 / 펌웨어 / 역추적 / QR
// 기존 'history' (timeline) 는 'deployments' 탭이 흡수.
type Tab =
  | 'overview'
  | 'deployments'
  // windows-ux-edit Round 2 Phase B (슬라이드4) — '계약' 탭 제거(개요의 '현재 고객사'
  // 로 충분). ContractTab 정의는 RobotDetailTabs 에 보존(비파괴).
  | 'manufacture'
  | 'field-service'
  | 'firmware'
  // docs/plan/v0.4.0-parts-solution-defect-lot/plan.md §6.4 — 부품 탭.
  // (솔루션 탭은 S2 §9.3/Phase 2.8에서 매장 단위로 이동하며 제거.)
  | 'parts'
  // docs/plan/loader-history-traceability-2026-07 — 로더(로봇 팔) 전용 탭.
  | 'loader'
  | 'trace'
  | 'qr';

const ROBOT_TABS: Tab[] = [
  'overview',
  'deployments',
  'manufacture',
  'field-service',
  'firmware',
  'parts',
  'loader',
  'trace',
  'qr',
];

// Per-kind affordances for the Robot timeline (plan §3.6).
//
// Status events are intentionally muted (most common, low-attention
// signal). Deployments take the brand color (high-attention,
// frequently the reason an operator opened the page). Transfers and
// QR events are rare but consequential — distinct accent colors
// help them stand out in a long list.
const TIMELINE_KIND_LABEL: Record<string, string> = {
  status: '상태',
  deployment: '배치',
  transfer: '이관',
  qr: 'QR',
};

const TIMELINE_DOT_COLOR: Record<string, string> = {
  status: 'var(--fg-muted)',
  deployment: 'var(--brand)',
  transfer: 'var(--dday-warning)',
  qr: 'var(--robot-shipping)',
};

/**
 * 개선 9건 차수2(26-08-04) §3 — 이력 한 줄의 작성자.
 *
 * 서버(robots.service timeline)가 `detail.actorName` 으로 표기를 완성해 내려준다
 * (이름 / '홍길동 (삭제된 계정)' / '—'). 화면은 **판정하지 않고 그대로 쓴다** — 같은
 * 판정을 여기서 다시 하면 서버와 갈린다. `detail` 은 자유 payload(Record<string,
 * unknown>)라 형태만 확인한다.
 */
function timelineActorName(detail: Record<string, unknown> | undefined): string | null {
  const v = detail?.actorName;
  return typeof v === 'string' && v.length > 0 ? v : null;
}

const TAB_LABELS: Record<Tab, string> = {
  overview: '개요',
  deployments: '출고 이력',
  manufacture: '제조 이력',
  'field-service': '고장 이력',
  firmware: '펌웨어',
  // docs/plan/v0.4.0-parts-solution-defect-lot/plan.md §6.4 — 부품 탭.
  parts: '부품',
  // docs/plan/loader-history-traceability-2026-07 — 로더 전용 탭.
  loader: '로더',
  trace: '교차 참조',
  qr: 'QR 스티커',
};

/** 'YYMM'(2511) → '2025.11'. 모양이 다르면 그대로. */
function formatYm(ym: string | null | undefined): string | null {
  if (!ym) return null;
  return /^\d{4}$/.test(ym) ? `20${ym.slice(0, 2)}.${ym.slice(2)}` : ym;
}

/** 로봇 QR 그림 — QR 스티커 탭과 같은 서버 그림(인증 불요). */
function robotQrPngUrl(serial: string, size: number): string {
  const apiBase =
    (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL ?? '/api/v1';
  return `${apiBase}/robots/${encodeURIComponent(serial)}/qrs/png?size=${size}`;
}

/** 접힌 '구분' 토큰 → 화면 단어(plan 2-5). 정본 두 단어 + '확인 필요'/빈 값. */
function formatKind(kind: string | null | undefined): string {
  if (kind === 'SALE') return CONTRACT_KIND_SALE;
  if (kind === 'SUBSCRIPTION') return CONTRACT_KIND_SUBSCRIPTION;
  if (kind === 'AMBIGUOUS') return '확인 필요';
  return CONTRACT_REASON_EMPTY;
}

export function RobotDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  // docs/work_log — Tab state in URL (?tab=...) so refresh + deep links
  // preserve the selected tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') ?? 'overview';
  const tab: Tab = ROBOT_TABS.includes(rawTab as Tab) ? (rawTab as Tab) : 'overview';
  // If URL contains an invalid `?tab=...` value (e.g. typo / stale deep link),
  // strip it so the tablist is keyboard-reachable again and the URL reflects
  // the actually-rendered tab.
  useEffect(() => {
    if (rawTab !== tab) {
      const params = new URLSearchParams(searchParams);
      params.delete('tab');
      setSearchParams(params, { replace: true });
    }
  }, [rawTab, tab]);
  function setTab(next: Tab) {
    const params = new URLSearchParams(searchParams);
    if (next === 'overview') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  }
  function handleTabKeyDown(e: KeyboardEvent<HTMLButtonElement>, currentTab: Tab) {
    const idx = ROBOT_TABS.indexOf(currentTab);
    if (idx < 0) return;
    let next: Tab | null = null;
    if (e.key === 'ArrowRight') next = ROBOT_TABS[(idx + 1) % ROBOT_TABS.length];
    else if (e.key === 'ArrowLeft')
      next = ROBOT_TABS[(idx - 1 + ROBOT_TABS.length) % ROBOT_TABS.length];
    else if (e.key === 'Home') next = ROBOT_TABS[0];
    else if (e.key === 'End') next = ROBOT_TABS[ROBOT_TABS.length - 1];
    if (next) {
      e.preventDefault();
      const target = next;
      setTab(target);
      requestAnimationFrame(() => {
        document.getElementById(`tab-${target}`)?.focus();
      });
    }
  }
  const [editOpen, setEditOpen] = useState(false);
  // 라운드15 결정3 — 상태머신을 우회하는 '상태 정정'. 일반 편집과는 다른 모달.
  const [correctionOpen, setCorrectionOpen] = useState(false);
  // 폐기 취소 동선(plan 1-1) — '폐기 취소' 버튼은 되돌릴 상태(보관 중)가 선택된
  // 채 상태 정정 모달을 연다. 일반 '상태 정정' 버튼은 현재 상태로 연다.
  const [correctionInitialStatus, setCorrectionInitialStatus] = useState<RobotStatus | undefined>(
    undefined,
  );
  // 구분 수동 정정(plan 2-5) · 사후 계약 연결/해제(plan 2-2).
  const [kindOpen, setKindOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const [deployOpen, setDeployOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // docs/work_log/p0-service-order-create-modal — RobotDetail 헤더 진입점.
  const [soCreateOpen, setSoCreateOpen] = useState(false);
  // S2 Phase 2.5 — 로봇 상세에서 계약으로의 역방향 배정 모달.
  const [assignContractOpen, setAssignContractOpen] = useState(false);
  // round5 #2d — 예약(배정) 해제 확인 다이얼로그.
  const [releaseOpen, setReleaseOpen] = useState(false);

  const userRole = useAuthStore((s) => s.user?.role);
  // 오너 결정 2026-10-20 — 직원도 허니컴로보틱스 로봇을 보고 고친다. 로봇·예약 계약의 법인과
  // 내 법인을 견줘야 하는 자리(아래 법인 표시 · '다른 법인 계약' 잠금)가 있어 사용자 전체를 읽는다.
  const authUser = useAuthStore((s) => s.user);
  // round12 버그1 — 목록과 동일하게 phase-1 초기값(ADMIN/MANAGER)이 남아 제조팀이
  // 상세에서도 편집 버튼을 못 봤다. 서버는 이미 MFG_MANAGER 의 update 를 허용.
  const canWrite = userRole ? canEditMfg(userRole) : false;
  // round11 요구5 — 배치/회수는 제조팀 + 필드팀까지 허용(canWrite 는 ADMIN/MANAGER
  // 뿐이라 팀 매니저 전원이 서버 권한과 무관하게 버튼조차 못 봤다).
  const canManageDeployment = userRole ? canDeployRobot(userRole) : false;
  // round12 결정4 — 제조팀도 로봇을 삭제할 수 있다. 단 여기서 여는 건 소프트 삭제
  // (휴지통)뿐 — 복구/완전삭제는 목록의 '삭제됨 포함' 뷰에서 ADMIN 전용으로 남는다.
  const canDelete = userRole ? canDeleteRobot(userRole) : false;
  // S2 Phase 2.5 — 제조팀(ADMIN/MFG_MANAGER/MANAGER) 만 계약 배정 가능.
  const canAssignToContract = userRole ? canEditMfg(userRole) : false;
  // 라운드15 결정3 — 상태 정정(관리자/제조팀). 폐기 계열 선택지만 모달 안에서
  // 다시 ADMIN 으로 좁힌다(서버 가드와 동일한 조건).
  const canCorrectStatus = userRole ? canCorrectRobotStatus(userRole) : false;

  // round5 #2d — 배정 해제. release 가 handoffStatus 를
  // SIGNED_AWAITING_ASSIGNMENT 로 되돌려 즉시 재배정 가능해진다.
  const releaseM = useMutation({
    mutationFn: (contractId: string) => assignmentsApi.release(contractId, id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['robot', id] });
      qc.invalidateQueries({ queryKey: ['robots'] });
      qc.invalidateQueries({ queryKey: ['assignments'] });
      // round11 요구6 — 해제가 '설치 대기' 로봇을 '출고 대기'로 되돌리므로
      // 파이프라인 카운트·타임라인도 함께 stale 이다.
      qc.invalidateQueries({ queryKey: ['robots-count'] });
      qc.invalidateQueries({ queryKey: ['robot-timeline', id] });
      setReleaseOpen(false);
      toast.success('배정을 해제했습니다. 다른 계약에 다시 배정할 수 있습니다.', '배정 해제');
    },
    onError: (err) => toast.error(extractApiError(err), '배정 해제 실패'),
  });

  const del = useMutation({
    mutationFn: () => robotsApi.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['robots'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('로봇을 삭제했습니다.', '삭제 완료');
      navigate('/robots', { replace: true });
    },
    onError: (err) => toast.error(extractApiError(err), '삭제 실패'),
  });

  const {
    data: robot,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['robot', id],
    queryFn: () => robotsApi.get(id!),
    enabled: Boolean(id),
  });

  // 지금 나가 있는 배치 — 스페어 전환(2026-10-28 오너 "이미 배치 처리돼있는 로봇 중에 스페어로 배치 처리를 해야하는
  // 로봇 … 로봇 상세에서 설정 가능하게"). 출고 이력 탭과 같은 조회라 캐시를 같이 쓴다.
  const depsQ = useQuery({
    queryKey: ['robot-deployments', id],
    queryFn: () => deploymentsApi.list({ robotId: id!, pageSize: 100 }),
    enabled: Boolean(id) && robot?.currentStore != null,
  });
  const activeDeployment = (depsQ.data?.items ?? []).find((d) => !d.returnedAt) ?? null;
  // 명판의 QR — QR 스티커 탭과 같은 조회(캐시 공유). 발급한 QR 이 있을 때만 그림을 띄운다.
  const qrQ = useQuery({
    queryKey: ['robot-qrs', id],
    queryFn: () => robotQrsApi.list(id!),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
  // 고객사 상세 '로봇 정보'의 [스페어 지정]과 같은 API · 같은 문구 — 표시만 바꾼다(로봇 상태 · 계약은 그대로).
  const spareM = useMutation({
    mutationFn: (v: { deploymentId: string; isSpare: boolean }) =>
      deploymentsApi.setSpare(v.deploymentId, v.isSpare),
    onSuccess: (_res, v) => {
      qc.invalidateQueries({ queryKey: ['deployments'] });
      qc.invalidateQueries({ queryKey: ['robot-deployments'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success(
        v.isSpare
          ? '스페어로 전환했습니다. 이제 설치 대수에 포함되지 않습니다.'
          : '스페어 지정을 해제했습니다. 다시 설치 대수에 포함됩니다.',
      );
    },
    onError: (err) => toast.error(extractApiError(err), '스페어 전환 실패'),
  });

  if (isLoading) return <div className="kuun-caption">불러오는 중…</div>;
  if (error) return <div style={{ color: 'var(--fg-danger)' }}>{extractApiError(error)}</div>;
  if (!robot) return null;

  const sn = robot.serialShort ? formatRobotShortId(robot.serialShort) : (robot.serialLong ?? '–');

  // 오너 지적(2026-08-31) "#113번의 기록을 찾을 수가 없음" — 이 로봇에서 그 로봇의
  // 기록으로 바로 가는 길. 번호가 있으면 번호로 보낸다(감사 화면의 '로봇 번호' 칸이
  // 그 번호로 채워져, 다음엔 번호로 찾는 길을 사람이 알게 된다). 짧은 번호도 긴
  // 시리얼도 없는 로봇(둘 다 nullable)은 내부 id 로 보낸다 — 길이 끊기지 않게.
  const auditSerial = robot.serialShort ?? robot.serialLong;
  const auditHref = auditSerial
    ? `/audit-logs?robotSerial=${encodeURIComponent(auditSerial)}`
    : `/audit-logs?resourceType=Robot&resourceId=${id ?? ''}`;

  // 초기 데이터 보정 — 배치/회수 가능 여부는 status 가 아니라 "활성 배치"
  // (currentStore) 유무로 판정한다. 자동 적재된 로봇은 status=DEPLOYED 이지만
  // 실제 Deployment(=currentStore)가 없어, 기존 status 기준에선 '배치' 버튼이
  // 숨고 '회수'만 떠(활성 배치가 없어 실패) 고객사 연결을 못 했다.
  // currentStore 는 Deployment 생성 시에만 세팅되고 회수 시 해제되므로
  // "활성 배치 존재"의 신뢰 가능한 신호다. 백엔드도 활성 배치가 없으면
  // status 와 무관하게 배치 생성을 허용한다(deployments.service 가드).
  const hasActiveDeployment = robot.currentStore != null;
  const canDeploy =
    canManageDeployment && robot.status !== RobotStatus.RETIRED && !hasActiveDeployment;
  // 2026-11-02 — 캘린더 이벤트의 [설치 완료]로 나간 로봇은 캘린더에서 거둔다(서버도 막는다).
  const eventDeployment = activeDeployment?.calendarEventId ? activeDeployment : null;
  const canReturn = canManageDeployment && hasActiveDeployment && !eventDeployment;
  // 스페어는 고객 배치에만(서버 규칙과 같다). 교육 · 전시 같은 임시 이동은 대상이 아니다.
  const spareTarget =
    hasActiveDeployment && activeDeployment?.kind === DeploymentKind.CUSTOMER
      ? activeDeployment
      : null;
  const canToggleSpare = canManageDeployment && spareTarget != null;
  // S2 Phase 2.5 — 재고 로봇(READY_TO_SHIP|IN_STORAGE)만 계약 배정 버튼 노출.
  // FB3 §5 (batch-15·결정 B) — DTO 의 reservedContractId 로 예약 여부를 직접 안다.
  // round5 #2d — (구 주석 "해제 엔드포인트가 없어 읽기전용"은 사실과 달랐음:
  // POST /manufacturing/assignments/:contractId/release 가 cross-tenant 가드·
  // handoffStatus 재계산까지 완비되어 있었고 UI 만 없었다.) 예약된 로봇에
  // '배정 해제' 버튼을 노출 — 해제하면 '계약에 배정' 버튼이 복귀해 재배정 완성.
  // 배치 전(예약) 단계 전용; 이미 배치(DEPLOYED)된 로봇은 회수 플로우.
  const isStockStatus =
    robot.status === RobotStatus.READY_TO_SHIP || robot.status === RobotStatus.IN_STORAGE;
  const isReserved = robot.reservedContractId != null;
  const showAssignToContract = canAssignToContract && isStockStatus && !isReserved;

  // 오너 결정 2026-10-20 — 직원도 허니컴로보틱스 로봇을 보고 고친다. 편집·삭제·상태 정정·회수·
  // 서비스 오더는 비욘드허니컴 로봇과 똑같이 둔다.
  // 오너 결정 2026-10-21 — 허니컴로보틱스 로봇도 비욘드허니컴 고객사에 배치("#14와 #124-2도
  // 고객사에 배치할 수 있게"). 10-20 에는 '배치'·'계약에 배정'·'계약에 연결'을 꺼 두었지만(같은
  // 법인 규칙에 걸려 직원이 고를 고객사·계약이 없었다), 이제 서버가 비욘드허니컴 고객사·계약에
  // 허니컴로보틱스 로봇을 받는다. 직원이 고르는 대상은 언제나 자기 법인 것이고, 자기가 볼 수 있는
  // 로봇은 자기 법인 고객사에 쓸 수 있으므로(robot-entity robotUsableAt) 세 버튼은 로봇의 법인과
  // 상관없이 켠다. 소유 법인은 그대로다(배치·배정은 장부를 옮기지 않는다).
  //
  // 남은 잠금은 하나 — 로봇을 붙든 **계약**이 다른 법인 것일 때(로봇의 법인과 별개로 본다).
  // 계약은 넓히지 않았으므로 '배정 해제'는 서버가 계약을 못 찾고 '계약 연결 해제'는 403 이다.
  // 영문 'Not Found' 를 보이며 실패하게 두지 않고 꺼 두고 이유를 붙인다(관리자는 법인을 넘으므로
  // 켜진다). 같은 이유로 그 계약의 고객사는 링크가 아니라 이름만 적는다(아래 '할당 완료' 배지).
  const otherEntityContract = isOtherEntityContract(authUser, robot.reservedContract);
  // 내 계정과 법인이 다른 로봇이면 머리에 법인 이름을 붙인다(관리자 포함) — 목록에서 넘어온
  // 사람이 "이 로봇은 장부상 다른 회사 것"임을 상세 첫 화면에서 알게.
  const showEntityChip = authUser != null && robot.ownerEntity !== authUser.ownerEntity;

  // §③ Phase 3 — backend RobotStatus → design 6-stage lifecycle.
  const currentStage = robotStatusToStage(robot.status);

  const extra = robot as RobotSummaryDto & {
    color?: string | null;
    manufactureCompletedAt?: string | Date | null;
    binaryUpdatedAt?: string | Date | null;
  };

  // 2026-11-02 오너 명판 A안 — 머리에는 자주 쓰는 동작만 꺼내 두고 나머지는 ⋯ 에 묶는다.
  //   꺼내 둔 것: 배치 · 회수(채운 단추 하나) · 서비스 오더 · 편집.
  //   ⋯ : 스페어 · 계약 배정/해제/연결 · 폐기 취소 · 변경 이력 | 상태 정정 · 삭제(경고색, 따로 묶음).
  const moreItems: RowMoreItem[] = [];
  if (canToggleSpare && spareTarget)
    moreItems.push({
      label: spareTarget.isSpare ? '스페어 해제' : '스페어로 전환',
      testId: 'robot-detail-spare',
      disabled: spareM.isPending,
      title: spareTarget.isSpare
        ? '스페어 지정을 해제하면 다시 설치 대수에 포함됩니다.'
        : '계약 대수와 별도로 둔 예비 로봇이면 스페어로 전환하세요. 배치는 그대로 두고 설치 대수에서만 뺍니다.',
      onClick: () => spareM.mutate({ deploymentId: spareTarget.id, isSpare: !spareTarget.isSpare }),
    });
  // S2 Phase 2.5 — 재고 로봇을 사인된 계약에 직접 배정.
  if (showAssignToContract)
    moreItems.push({ label: '계약에 배정', onClick: () => setAssignContractOpen(true) });
  // round5 #2d — 예약(배정) 해제. 재고 로봇만(나가 있는 로봇은 아래 '계약 연결 해제').
  if (canAssignToContract && isReserved && robot.reservedContract && isStockStatus)
    moreItems.push({
      label: '배정 해제',
      disabled: otherEntityContract,
      title: otherEntityContract ? OTHER_ENTITY_CONTRACT_HINT : undefined,
      onClick: () => setReleaseOpen(true),
    });
  // 사후 계약 연결(plan 2-2) — 이미 나가 있거나 판매 완료된 로봇의 장부 연결.
  if (canWrite && !isStockStatus && !isReserved)
    moreItems.push({ label: '계약에 연결', onClick: () => setLinkOpen(true) });
  if (canWrite && !isStockStatus && isReserved)
    moreItems.push({
      label: '계약 연결 해제',
      disabled: otherEntityContract,
      title: otherEntityContract ? OTHER_ENTITY_CONTRACT_HINT : undefined,
      onClick: () => setUnlinkOpen(true),
    });
  // 폐기 취소 동선(plan 1-1) — 폐기 예정/완료 로봇에서는 '상태 정정' 안의 그 길을 이름으로 보여 준다.
  if (
    canCorrectStatus &&
    (robot.status === RobotStatus.RETIREMENT_PLANNED || robot.status === RobotStatus.RETIRED)
  )
    moreItems.push({
      label: '폐기 취소',
      onClick: () => {
        setCorrectionInitialStatus(RobotStatus.IN_STORAGE);
        setCorrectionOpen(true);
      },
    });
  // 오너 지적(2026-08-31) — '이 로봇을 누가 뭘 바꿨나' 직행. 감사 로그는 ADMIN 전용 라우트.
  if (userRole === UserRole.ADMIN)
    moreItems.push({
      label: '변경 이력',
      testId: 'robot-audit-link',
      title: '감사 로그에서 이 로봇의 변경 이력을 봅니다',
      separatorBefore: moreItems.length > 0,
      href: auditHref,
    });
  // 라운드15 결정3 — 전이 규칙을 건너뛰는 예외 조작은 경고색으로 따로 묶는다.
  const dangerStart = moreItems.length;
  if (canCorrectStatus)
    moreItems.push({
      label: '상태 정정',
      danger: true,
      onClick: () => {
        setCorrectionInitialStatus(undefined);
        setCorrectionOpen(true);
      },
    });
  if (canDelete)
    moreItems.push({ label: '삭제', danger: true, onClick: () => setDeleteOpen(true) });
  if (moreItems.length > dangerStart && dangerStart > 0)
    moreItems[dangerStart].separatorBefore = true;

  // 명판 — 상태 글자는 상태색, '어디'는 나가 있으면 그 매장 · 아니면 위치(창고 · 공장 …).
  const statusTone = ROBOT_STATUS_TINT[robot.status] ?? {
    color: 'var(--fg-muted)',
    tint: 'var(--bg-sunken)',
  };
  const locationLabel = ROBOT_LOCATION_LABELS[robot.locationCategory] ?? robot.locationCategory;
  const ownerName = robot.ownerEntity === 'BEYOND_HONEYCOMB' ? '비욘드허니컴' : '허니컴로보틱스';
  const reservedCustomer = robot.reservedContract?.customerDisplayName ?? null;
  const place = robot.currentStore?.name ?? locationLabel;
  const placeSub = robot.currentStore
    ? locationLabel
    : isReserved && reservedCustomer
      ? `${reservedCustomer}에 나갈 예정`
      : null;
  const snDigits = sn.startsWith('#') ? sn.slice(1) : sn;
  const qrSerial = robot.serialShort ?? robot.serialLong;
  const activeQr = (qrQ.data?.items ?? []).find((q) => q.active) ?? null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Link to="/robots" className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
        ← 로봇 목록
      </Link>

      <section className="rd-plate" aria-label={`로봇 ${sn}`}>
        <div className="rd-plate-top">
          <div>
            <div className="rd-sn">
              {sn.startsWith('#') && <span className="rd-hash">#</span>}
              {snDigits}
            </div>
            <div className="rd-model">
              <b>{robot.model.code}</b>
              <span className="rd-bar" aria-hidden />
              {ownerName}
            </div>
          </div>

          <div
            className="rd-state"
            style={{ '--sc': statusTone.color, '--st': statusTone.tint } as CSSProperties}
          >
            <div className="rd-state-word" data-testid="robot-detail-status">
              {ROBOT_STATUS_LABELS[robot.status] ?? robot.status}
            </div>
            <div className="rd-state-place">{place}</div>
            {placeSub && <div className="rd-state-sub">{placeSub}</div>}
            <div className="rd-state-notes">
              {/* FB3 §5 — 계약 예약된 로봇은 "할당 완료"(읽기전용). */}
              {isReserved && robot.reservedContract && (
                <span role="status" className="rd-contract">
                  <Icon name="check" size={14} color="currentColor" strokeWidth={3} />
                  할당 완료
                  <span className="rd-mono" style={{ fontSize: 13 }}>
                    {robot.reservedContract.humanId}
                  </span>
                  {/* 오너 결정 2026-10-20 — 다른 법인 **계약**의 고객사는 직원에게 열리지 않는다. 링크로 두면
                      누르는 순간 '찾을 수 없음'이라 이름만 적는다. 로봇이 아니라 계약의 법인을 본다. */}
                  {robot.reservedContract.customerId &&
                    (otherEntityContract ? (
                      <span data-testid="robot-reserved-customer-text">
                        {reservedCustomer ?? '고객사'}
                      </span>
                    ) : (
                      <Link to={`/customers/${robot.reservedContract.customerId}`}>
                        {reservedCustomer ?? '고객사'}
                      </Link>
                    ))}
                </span>
              )}
              <VerificationPill status={robot.verificationStatus} />
              {spareTarget?.isSpare && <SpareBadge testId="robot-detail-spare-badge" />}
              {showEntityChip && (
                <span
                  data-testid="robot-entity-chip"
                  title="이 로봇의 소유 법인입니다. 내 계정과 다른 법인입니다."
                  className="rd-chip"
                  style={{ border: '1px solid var(--border-card)', color: 'var(--fg-muted)' }}
                >
                  {ownerName}
                </span>
              )}
            </div>
            {eventDeployment && (
              <div
                data-testid="robot-detail-event-deployment"
                className="rd-state-sub"
                style={{ marginTop: 8, maxWidth: 420, lineHeight: 1.5 }}
              >
                캘린더 이벤트{eventDeployment.eventName ? `(${eventDeployment.eventName})` : ''}로
                나가 있습니다. 회수는 통합 캘린더의 [이벤트 회수] 일정에서 합니다.
              </div>
            )}
          </div>

          <div className="rd-plate-right">
            {/* 로봇 몸의 QR 스티커 — 누르면 QR 스티커 탭. 발급한 QR 이 없으면 빈 자리만 둔다. */}
            <button
              type="button"
              className={activeQr && qrSerial ? 'rd-qr' : 'rd-qr none'}
              title="QR 스티커"
              aria-label="QR 스티커 탭 열기"
              onClick={() => setTab('qr')}
            >
              {activeQr && qrSerial ? (
                <img src={robotQrPngUrl(qrSerial, 160)} alt="" />
              ) : (
                <>
                  <Icon name="qr" size={18} />
                  QR 없음
                </>
              )}
            </button>
            <div className="rd-acts">
              {canDeploy && (
                <Button type="button" onClick={() => setDeployOpen(true)}>
                  배치
                </Button>
              )}
              {canReturn && (
                <Button type="button" onClick={() => setReturnOpen(true)}>
                  회수
                </Button>
              )}
              {/* 로봇 디테일에서 바로 SO 생성. FIELD 운영자도 가능하므로 조건 없음. */}
              <Button type="button" variant="secondary" onClick={() => setSoCreateOpen(true)}>
                + 서비스 오더
              </Button>
              {canWrite && (
                <Button type="button" variant="secondary" onClick={() => setEditOpen(true)}>
                  편집
                </Button>
              )}
              <RowMoreMenu
                items={moreItems}
                label="동작 더 보기"
                testId="robot-detail-more"
                size={32}
              />
            </div>
          </div>
        </div>

        <div className="rd-plate-bottom">
          <LifecycleTrack current={currentStage} />
        </div>
      </section>

      <div role="tablist" aria-label="로봇 상세 탭" className="rd-tabs">
        {ROBOT_TABS.map((t) => (
          <TabButton
            key={t}
            tabKey={t}
            active={tab === t}
            onClick={() => setTab(t)}
            onKeyDown={(e) => handleTabKeyDown(e, t)}
          >
            {TAB_LABELS[t]}
          </TabButton>
        ))}
      </div>

      {tab === 'overview' && (
        <div role="tabpanel" id="panel-overview" aria-labelledby="tab-overview">
          <Sheet className="rd-overview">
            {/* §③ Phase 3 — 최근 이력. */}
            {id ? <RobotTimelinePanel robotId={id} canEdit={canWrite} /> : <div />}
            <div className="rd-facts">
              <FactSection title="식별 정보">
                <FactList>
                  <Fact k="Short SN" v={robot.serialShort} mono />
                  <Fact k="Long SN" v={robot.serialLong} mono />
                  <Fact k="회계 번호" v={robot.accountingBookNo} mono />
                  <Fact k="회계 방식" v={robot.accountingScheme} />
                </FactList>
              </FactSection>
              <FactSection title="소유 · 위치">
                <FactList>
                  <Fact k="소유 법인" v={ownerName} />
                  {/* windows-ux-edit Round 2 Phase B — '현재 고객사'로 단일화. 배치 매장의 고객사 우선,
                      없으면 예약 계약 고객사. 상태 · 위치는 명판에 있어 여기서는 뺐다. */}
                  <Fact k="현재 고객사" v={robot.currentStore?.customerName ?? reservedCustomer} />
                  {/* 구분 수동 정정(plan 2-5) — 최종 표시값 + 출처. 수동이면 자동 계산값을 함께 적는다. */}
                  <Fact
                    k="구분"
                    v={
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'baseline',
                          gap: 8,
                          flexWrap: 'wrap',
                        }}
                      >
                        {formatKind(robot.contractKind)}
                        {robot.contractKindSource === 'MANUAL' && (
                          <span
                            data-testid="detail-contract-kind-manual"
                            style={{ fontSize: 12, fontWeight: 500, color: 'var(--fg-warning)' }}
                          >
                            수동
                            {robot.contractKindAuto !== robot.contractKind
                              ? `, 자동 계산은 ${formatKind(robot.contractKindAuto)}`
                              : ''}
                          </span>
                        )}
                        {canWrite && (
                          <button
                            type="button"
                            onClick={() => setKindOpen(true)}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              color: 'var(--fg-brand)',
                              cursor: 'pointer',
                              fontSize: 13,
                              fontWeight: 550,
                            }}
                          >
                            정정
                          </button>
                        )}
                      </span>
                    }
                  />
                </FactList>
              </FactSection>
              <FactSection title="제조 · 출고">
                <FactList>
                  <Fact k="첫 제조 월" v={formatYm(robot.firstProductionYm)} mono />
                  <Fact k="첫 출고 월" v={formatYm(robot.firstShipmentYm)} mono />
                  <Fact k="모델 코드" v={robot.model.code} mono />
                  {/* §D-1 (PDF p.8) — 서버 응답에 있으면 보인다. 빈 값은 줄째 숨긴다. */}
                  {extra.color && <Fact k="색상" v={extra.color} />}
                  {/* FB2 §5C (결정 D) — "로더 종류"는 구분 사양과 겹쳐 상세에서 보이지 않는다. */}
                  {extra.manufactureCompletedAt && (
                    <Fact k="제조 완료일" v={kstYmd(extra.manufactureCompletedAt)} mono />
                  )}
                  {extra.binaryUpdatedAt && (
                    <Fact k="바이너리 업데이트" v={kstYmd(extra.binaryUpdatedAt)} mono />
                  )}
                </FactList>
              </FactSection>
              {/* docs/plan/loader-history-traceability — 지금 장착 로더 요약(상세는 로더 탭). */}
              {id && <CurrentLoaderFacts robotId={id} />}
              <FactSection title="메모">
                <p style={{ whiteSpace: 'pre-wrap' }}>
                  {robot.notes ?? <Empty text="메모 없음" />}
                </p>
              </FactSection>
            </div>
          </Sheet>
          {/* 인수증 섹션은 고객사 서류 탭으로 이전됨(acceptance-ux-changes-2026-06 §B4 / D5). */}
        </div>
      )}

      {tab === 'deployments' && id && (
        <div role="tabpanel" id="panel-deployments" aria-labelledby="tab-deployments">
          <DeploymentsTab robotId={id} robot={robot} />
        </div>
      )}
      {tab === 'manufacture' && (
        <div role="tabpanel" id="panel-manufacture" aria-labelledby="tab-manufacture">
          <ManufactureTab robot={robot} />
        </div>
      )}
      {tab === 'field-service' && id && (
        <div role="tabpanel" id="panel-field-service" aria-labelledby="tab-field-service">
          <FieldServiceTab robotId={id} />
        </div>
      )}
      {tab === 'firmware' && id && (
        <div role="tabpanel" id="panel-firmware" aria-labelledby="tab-firmware">
          <FirmwareTab robotId={id} />
        </div>
      )}
      {/* v0.4.0 §6.4 — 부품 / 솔루션 탭 (alpha — read only). */}
      {tab === 'parts' && id && (
        <div role="tabpanel" id="panel-parts" aria-labelledby="tab-parts">
          <RobotPartsTab robotId={id} />
        </div>
      )}
      {tab === 'loader' && id && (
        <div role="tabpanel" id="panel-loader" aria-labelledby="tab-loader">
          <LoaderTab robotId={id} />
        </div>
      )}
      {tab === 'trace' && (
        <div role="tabpanel" id="panel-trace" aria-labelledby="tab-trace">
          <TraceTab robot={robot} />
        </div>
      )}

      {tab === 'qr' && id && (
        <div role="tabpanel" id="panel-qr" aria-labelledby="tab-qr">
          <QRTab robotId={id} robotSerial={robot.serialShort ?? null} />
        </div>
      )}

      <RobotEditModal open={editOpen} onClose={() => setEditOpen(false)} robot={robot} />
      <RobotStatusCorrectionModal
        open={correctionOpen}
        onClose={() => setCorrectionOpen(false)}
        robot={robot}
        initialStatus={correctionInitialStatus}
      />
      <RobotContractKindModal open={kindOpen} onClose={() => setKindOpen(false)} robot={robot} />
      <RobotContractLinkModal open={linkOpen} onClose={() => setLinkOpen(false)} robot={robot} />
      <RobotContractUnlinkModal
        open={unlinkOpen}
        onClose={() => setUnlinkOpen(false)}
        robot={robot}
      />
      <ConfirmDialog
        open={deleteOpen}
        title="로봇 삭제"
        message={`${sn} 로봇을 삭제할까요? 목록에서 사라지며, 관리자는 로봇 목록의 「삭제됨 포함」에서 복구할 수 있습니다.`}
        confirmLabel="삭제"
        danger
        busy={del.isPending}
        onConfirm={() => del.mutate()}
        onCancel={() => setDeleteOpen(false)}
      />
      {id && (
        <>
          <DeployRobotModal open={deployOpen} onClose={() => setDeployOpen(false)} robotId={id} />
          <ReturnRobotModal open={returnOpen} onClose={() => setReturnOpen(false)} robotId={id} />
          <ServiceOrderCreateModal
            open={soCreateOpen}
            onClose={() => setSoCreateOpen(false)}
            defaultRobotId={id}
            onCreated={() => {
              // 생성 후 field-service 탭으로 점프해서 즉시 이력에 반영된 결과를
              // 보여줌. (FieldServiceTab 이 SO 이력을 표시하도록 P0-2 에서 보강)
              setTab('field-service');
              toast.success('서비스 오더를 만들었습니다. 서비스 오더 화면에서 확인해 주세요.');
            }}
          />
          {/* S2 Phase 2.5 — 로봇 → 계약 역방향 배정. */}
          <AssignToContractModal
            open={assignContractOpen}
            onClose={() => setAssignContractOpen(false)}
            robotId={id}
            robotSerial={sn}
          />
          {/* round5 #2d — 배정 해제 확인. 예약 단계 전용(배치 전). */}
          <ConfirmDialog
            open={releaseOpen}
            title="배정 해제"
            message={
              `이 로봇의 ${robot.reservedContract?.humanId ?? ''} 계약 배정을 해제할까요?\n\n` +
              `해제하면 로봇을 다른 계약에 다시 배정할 수 있고, 이 계약은 배정 인박스에 다시 나타납니다.\n` +
              (robot.status === RobotStatus.INSTALL_PENDING
                ? `로봇 상태는 '설치 대기'에서 '출고 대기'로 되돌아갑니다.\n`
                : '') +
              `이미 배치된 로봇을 회수하는 것이 아니라, 배치 전 예약을 푸는 것입니다.`
            }
            confirmLabel="배정 해제"
            danger
            busy={releaseM.isPending}
            onConfirm={() => {
              if (robot.reservedContractId) releaseM.mutate(robot.reservedContractId);
            }}
            onCancel={() => setReleaseOpen(false)}
          />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// S2 Phase 2.5 — 로봇 상세에서 사인된 계약으로의 역방향 배정 모달.
// eligible-contracts 를 조회해 목록으로 보여주고, 선택 시 reserve.
// 빈 목록이면 안내(예약됨/요구 모델 계약 없음 모두 여기서 처리).
// ─────────────────────────────────────────────────────────────────────────
function AssignToContractModal({
  open,
  onClose,
  robotId,
  robotSerial,
}: {
  open: boolean;
  onClose: () => void;
  robotId: string;
  robotSerial: string;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const eligibleQ = useQuery({
    queryKey: ['assignments', 'eligible-contracts', robotId],
    queryFn: () => assignmentsApi.eligibleContractsForRobot(robotId),
    enabled: open,
  });

  const assign = useMutation({
    mutationFn: (contractId: string) => assignmentsApi.assignRobotToContract(robotId, contractId),
    onSuccess: () => {
      toast.success('로봇을 계약에 배정했습니다.', '배정 완료');
      // 로봇 + 배정 쿼리 무효화.
      qc.invalidateQueries({ queryKey: ['robot', robotId] });
      qc.invalidateQueries({ queryKey: ['assignments'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'manufacturing'] });
      onClose();
    },
    onError: (err) => toast.error(extractApiError(err), '배정 실패'),
  });

  if (!open) return null;

  const contracts = eligibleQ.data ?? [];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="계약에 배정"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--bg-overlay)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 480,
          maxWidth: '90vw',
          maxHeight: '80vh',
          overflow: 'auto',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-md)',
          padding: 20,
        }}
      >
        <div className="kuun-h3" style={{ marginBottom: 4 }}>
          계약에 배정
        </div>
        <div className="kuun-caption" style={{ marginBottom: 14, color: 'var(--fg-muted)' }}>
          <span style={{ fontFamily: 'var(--font-mono)' }}>{robotSerial}</span> 로봇을 배정할 서명된
          계약을 골라 주세요.
        </div>

        {eligibleQ.isLoading ? (
          <div className="kuun-caption">불러오는 중…</div>
        ) : eligibleQ.error ? (
          <div style={{ color: 'var(--fg-danger)' }}>{extractApiError(eligibleQ.error)}</div>
        ) : contracts.length === 0 ? (
          <div
            className="kuun-caption"
            style={{
              padding: '16px',
              textAlign: 'center',
              color: 'var(--fg-muted)',
              border: '1px dashed var(--border-default)',
              borderRadius: 'var(--radius-md)',
              lineHeight: 1.6,
            }}
          >
            배정 가능한 계약이 없습니다.
            <br />이 모델이 필요한 <strong>계약 완료</strong> 상태의 배정 대기 계약이 없거나, 남은
            수량이 모두 예약됐습니다. 고객사 마스터에서 진행 상태를 ‘계약 완료’로 바꾸면 해당 계약이
            여기 표시됩니다.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {contracts.map((c) => (
              <button
                key={c.contractId}
                type="button"
                disabled={assign.isPending}
                onClick={() => assign.mutate(c.contractId)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 2,
                  padding: '10px 12px',
                  border: '1px solid var(--border-default)',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--bg-surface)',
                  cursor: assign.isPending ? 'wait' : 'pointer',
                  textAlign: 'left',
                }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', width: '100%' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{c.humanId}</span>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>
                    {c.customerDisplayName ?? '고객사 정보 없음'}
                  </span>
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontSize: 11,
                      color: 'var(--fg-muted)',
                    }}
                  >
                    남은 {c.remainingForModel}대
                  </span>
                </div>
                <div className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
                  설치 예정: {kstYmd(c.scheduledInstallAt) ?? '미정'}
                </div>
              </button>
            ))}
          </div>
        )}

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="button" variant="secondary" onClick={onClose}>
            닫기
          </Button>
        </div>
      </div>
    </div>
  );
}

// Timeline filter — `'all'` plus the four kinds the API can emit.
// Kept as a string union (not the TS literal of TimelineEntry.kind)
// so future kinds added at the API layer don't silently widen this
// filter without an explicit Windows UI update.
type TimelineFilter = 'all' | 'status' | 'deployment' | 'transfer' | 'qr';
const ORDERED_KINDS: ReadonlyArray<Exclude<TimelineFilter, 'all'>> = [
  'status',
  'deployment',
  'transfer',
  'qr',
];

// §D-4 — DeploymentsTab + dedicated firmware/field/contract tabs
// replaced the old monolithic timeline view. _HistoryTab is kept
// as a reference implementation in case the unified timeline is
// re-introduced later (underscore prefix opts out of the lint
// rule for unused locals).
function _HistoryTab({ robotId }: { robotId: string }) {
  const [filter, setFilter] = useState<TimelineFilter>('all');

  const timelineQ = useQuery({
    queryKey: ['robot-timeline', robotId],
    queryFn: async () => {
      const { data } = await apiClient.get<RobotTimelineResponse>(`/robots/${robotId}/timeline`);
      return data;
    },
  });

  if (timelineQ.isLoading) {
    return (
      <InfoCard title="이력 타임라인">
        <span className="kuun-caption">불러오는 중…</span>
      </InfoCard>
    );
  }

  if (timelineQ.error) {
    return (
      <InfoCard title="이력 타임라인">
        <span style={{ color: 'var(--fg-danger)' }}>{extractApiError(timelineQ.error)}</span>
      </InfoCard>
    );
  }

  const entries = timelineQ.data?.entries ?? [];
  if (entries.length === 0) {
    return (
      <InfoCard title="이력 타임라인">
        <span className="kuun-caption">이력이 없습니다.</span>
      </InfoCard>
    );
  }

  // Per-kind counts drive the chip badges + decide which chips to
  // render at all (no point offering "QR (0)" filter if the robot
  // has never had a QR sticker issued).
  const counts: Record<string, number> = { all: entries.length };
  for (const e of entries) {
    counts[e.kind] = (counts[e.kind] ?? 0) + 1;
  }

  const visibleEntries = filter === 'all' ? entries : entries.filter((e) => e.kind === filter);

  return (
    <InfoCard title="이력 타임라인">
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          marginBottom: 8,
        }}
      >
        <FilterChip
          label="전체"
          count={counts.all ?? 0}
          active={filter === 'all'}
          onClick={() => setFilter('all')}
        />
        {ORDERED_KINDS.map((k) =>
          counts[k] ? (
            <FilterChip
              key={k}
              label={TIMELINE_KIND_LABEL[k] ?? k}
              count={counts[k] ?? 0}
              active={filter === k}
              dotColor={TIMELINE_DOT_COLOR[k]}
              onClick={() => setFilter(k)}
            />
          ) : null,
        )}
      </div>

      {visibleEntries.length === 0 ? (
        <span className="kuun-caption">선택한 종류의 이력이 없습니다.</span>
      ) : (
        <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {visibleEntries.map((e) => (
            <li
              key={e.id}
              style={{
                display: 'flex',
                gap: 12,
                padding: '10px 0',
                borderTop: '1px dashed var(--border-subtle)',
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  marginTop: 7,
                  background: TIMELINE_DOT_COLOR[e.kind] ?? 'var(--fg-muted)',
                  flexShrink: 0,
                }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{e.summary}</div>
                <div
                  className="kuun-caption"
                  style={{ marginTop: 2, fontFamily: 'var(--font-mono)', color: 'var(--fg-muted)' }}
                >
                  {/* 추가수정 2차(26-07-27) §2 — 브라우저 로캘 대신 KST 고정. */}
                  {kstDateTime(e.occurredAt) ?? '–'}
                  {/* 개선 9건 차수2(26-08-04) §3 — '누가'. 개요 탭의 '최근 이력'
                      카드와 같은 출처(detail.actorName)를 쓴다. */}
                  {timelineActorName(e.detail) ? ` · ${timelineActorName(e.detail)}` : ''}
                </div>
              </div>
              <div
                className="kuun-caption"
                style={{
                  fontSize: 10,
                  padding: '2px 6px',
                  borderRadius: 999,
                  background: 'var(--bg-sunken)',
                  color: 'var(--fg-muted)',
                  alignSelf: 'flex-start',
                  marginTop: 4,
                }}
              >
                {TIMELINE_KIND_LABEL[e.kind] ?? e.kind}
              </div>
            </li>
          ))}
        </ol>
      )}
    </InfoCard>
  );
}

// §③ Phase 3 — Hero 하단 Timeline 카드 (개요 탭 끝).
// 최근 5개의 status/deployment/transfer/qr 이벤트 — Phase 1 의 <Timeline /> 컴포넌트 사용.
// 전체 이력은 "출고 이력" 탭 (DeploymentsTab) 에 있음.
/** 개요의 최근 이력 — 처음엔 이만큼만, [모두 보기]로 펼친다. */
const OVERVIEW_TIMELINE_LIMIT = 6;

// 2026-11-02 오너 — '최근 이력' 편집: [편집]을 누르면 편집 모드(주황 테두리 · 편집 중 배지)로 바뀐다.
//   · 줄을 누르면 "해당 시점으로 되돌리시겠습니까?" — 그 뒤 줄은 숨겨지고(감사 로그에만 남는다) 로봇 상태도 그때로.
//   · [지우기]는 그 줄만 숨긴다(상태는 그대로).
//   · 숨긴 줄은 편집 모드에서 줄이 그어져 보이고, 지운 줄은 [다시 살리기], 되돌리기는 위 띠의 [되돌리기 취소].
// 권한은 로봇을 고칠 수 있는 직원 모두(서버 update 권한 = 콘솔 canWrite).
type HistoryDialog = { type: 'rewind' | 'hide'; entry: RobotTimelineEntry; laterCount: number };

function RobotTimelinePanel({ robotId, canEdit }: { robotId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [dialog, setDialog] = useState<HistoryDialog | null>(null);
  const timelineQ = useQuery({
    // Sub-key of ['robot-timeline', robotId] (NOT the sibling key
    // 'robot-timeline-overview') so that every existing invalidation —
    // RobotEditModal update/transfer, DeployRobotModal, QRTab, ReturnRobotModal,
    // and the realtime layer, which all invalidate ['robot-timeline', robotId] —
    // partial-matches and refreshes this "최근 이력" card. Previously the
    // distinct first key element meant none of them hit this panel, so it stayed
    // stale (e.g. an ownership transfer didn't appear until a hard refresh).
    queryKey: ['robot-timeline', robotId, 'overview', editing ? 'edit' : 'view'],
    queryFn: async () => {
      const { data } = await apiClient.get<RobotTimelineResponse>(
        `/robots/${robotId}/timeline${editing ? '?edit=1' : ''}`,
      );
      return data;
    },
    staleTime: 30_000,
  });
  const [expanded, setExpanded] = useState(false);
  const entries = timelineQ.data?.entries ?? [];
  const live = entries.filter((e) => !e.hidden);
  const newestId = live[0]?.id;
  const hiddenCount = timelineQ.data?.hiddenCount ?? 0;
  const latestRewind = timelineQ.data?.rewinds?.[0] ?? null;
  const rewindTarget = latestRewind
    ? entries.find((e) => e.id === latestRewind.targetEntryId)
    : undefined;
  // 편집 중에는 숨긴 줄까지 모두 펼친다 — 고를 줄이 접혀 있으면 안 된다.
  const rows = editing || expanded ? entries : entries.slice(0, OVERVIEW_TIMELINE_LIMIT);

  // 되돌리기는 배치 · 계약 · 고객사까지 바꾼다 — 로봇 상세가 읽는 것을 모두 새로 읽는다.
  const refresh = () => {
    for (const key of [
      ['robot-timeline', robotId],
      ['robot', robotId],
      ['robots'],
      ['robot-deployments'],
      ['deployments'],
      ['field-remediations'],
      ['service-orders'],
      ['dashboard'],
    ])
      void qc.invalidateQueries({ queryKey: key });
  };
  // 막히면 확인창을 닫고 이유를 알린다 — 안내대로 다른 줄을 먼저 정리할 수 있게.
  const fail = (title: string) => (err: unknown) => {
    setDialog(null);
    toast.error(extractApiError(err), title);
  };

  const rewindM = useMutation({
    mutationFn: (entryId: string) =>
      apiClient
        .post<{
          hiddenCount: number;
          status: string;
          cancelledServiceOrders?: string[];
          keptServiceOrders?: string[];
        }>(`/robots/${robotId}/history/rewind`, { entryId })
        .then((r) => r.data),
    onSuccess: (res, entryId) => {
      setDialog(null);
      refresh();
      const at = entries.find((e) => e.id === entryId);
      // 풀린 회수가 만든 회수 정비 오더(2026-11-03 오너 "서비스 오더도 같이 취소되게").
      const cancelled = res.cancelledServiceOrders ?? [];
      const kept = res.keptServiceOrders ?? [];
      const orders = [
        cancelled.length ? ` 회수 정비 오더 ${cancelled.join(', ')}도 취소했습니다.` : '',
        kept.length
          ? ` 회수 정비 오더 ${kept.join(', ')}는 이미 진행 중이라 그대로 두었습니다.`
          : '',
      ].join('');
      toast.success(
        `${at ? (kstDateTime(at.occurredAt) ?? '').split(' ')[0] : '그'} 시점으로 되돌렸습니다. 숨긴 줄 ${res.hiddenCount}개, 지금 상태는 ${ROBOT_STATUS_LABELS[res.status as RobotStatus] ?? res.status}입니다.${orders}`,
        '이력 되돌리기',
      );
    },
    onError: fail('되돌리지 못했습니다'),
  });
  const hideM = useMutation({
    mutationFn: (entryId: string) =>
      apiClient.post(`/robots/${robotId}/history/hide`, { entryId }).then((r) => r.data),
    onSuccess: () => {
      setDialog(null);
      refresh();
      toast.success('그 줄을 지웠습니다. 로봇 상태는 그대로입니다.', '이력 지우기');
    },
    onError: fail('지우지 못했습니다'),
  });
  const revertM = useMutation({
    mutationFn: (edit: { id: string; kind: string }) =>
      apiClient
        .post<{
          reopenedServiceOrders?: string[];
        }>(`/robots/${robotId}/history/edits/${edit.id}/revert`)
        .then((r) => ({ ...edit, reopened: r.data?.reopenedServiceOrders ?? [] })),
    onSuccess: (edit) => {
      refresh();
      toast.success(
        edit.kind === 'REWIND'
          ? `되돌리기를 취소했습니다. 숨긴 줄과 로봇 상태가 돌아왔습니다.${
              edit.reopened.length
                ? ` 회수 정비 오더 ${edit.reopened.join(', ')}도 다시 열었습니다.`
                : ''
            }`
          : '그 줄을 다시 살렸습니다.',
        '이력 편집 취소',
      );
    },
    onError: fail('다시 살리지 못했습니다'),
  });
  const busy = rewindM.isPending || hideM.isPending || revertM.isPending;

  return (
    <section
      aria-label="최근 이력"
      className={editing ? 'rd-history editing' : 'rd-history'}
      style={{ minWidth: 0 }}
      data-testid="robot-history"
    >
      <SheetHead
        title="최근 이력"
        note={
          editing ? (
            <span className="rd-edit-badge">
              <Icon name="pen" size={12} aria-hidden />
              편집 중
            </span>
          ) : hiddenCount > 0 ? (
            <span className="rd-count" data-testid="timeline-hidden-count">
              숨긴 줄 {hiddenCount}개
            </span>
          ) : null
        }
        actions={
          canEdit && (
            <Button
              type="button"
              variant={editing ? 'primary' : 'secondary'}
              style={smallBtn}
              aria-pressed={editing}
              // 머리의 로봇 [편집]과 이름이 겹치지 않게.
              aria-label={editing ? '최근 이력 편집 끝' : '최근 이력 편집'}
              onClick={() => setEditing((v) => !v)}
              data-testid="timeline-edit-toggle"
            >
              {editing ? (
                '편집 끝'
              ) : (
                <>
                  <Icon name="pen" size={14} aria-hidden />
                  편집
                </>
              )}
            </Button>
          )
        }
      />
      {editing && (
        <>
          <p className="rd-edit-guide">
            <strong>줄을 누르면</strong> 그 시점으로 되돌립니다. 그 뒤의 이력은 숨기고 로봇 상태도
            그때로 돌아갑니다. <strong>지우기</strong>는 그 줄만 숨기고 상태는 그대로 둡니다. 숨긴
            줄은 여기서 다시 살릴 수 있습니다.
          </p>
          {latestRewind && (
            <div className="rd-edit-rewound" data-testid="timeline-rewound">
              <Icon name="undo" size={15} aria-hidden />
              <span>
                {rewindTarget ? (
                  <>
                    <span className="rd-mono">
                      {(kstDateTime(rewindTarget.occurredAt) ?? '').split(' ')[0]}
                    </span>{' '}
                    &lsquo;{rewindTarget.summary}&rsquo; 시점으로 되돌린 상태입니다.
                  </>
                ) : (
                  '이전 시점으로 되돌린 상태입니다.'
                )}
              </span>
              <Button
                type="button"
                variant="secondary"
                style={smallBtn}
                disabled={busy}
                onClick={() => revertM.mutate({ id: latestRewind.id, kind: 'REWIND' })}
                data-testid="timeline-rewind-cancel"
              >
                되돌리기 취소
              </Button>
            </div>
          )}
        </>
      )}
      {timelineQ.isLoading ? (
        <SheetEmpty>불러오는 중…</SheetEmpty>
      ) : timelineQ.error ? (
        <SheetEmpty>
          <span style={{ color: 'var(--fg-danger)' }}>{extractApiError(timelineQ.error)}</span>
        </SheetEmpty>
      ) : entries.length === 0 ? (
        <SheetEmpty>
          <span data-testid="timeline-empty">이력이 없습니다.</span>
        </SheetEmpty>
      ) : (
        <HistoryList
          items={rows.map((e) => {
            // 추가수정 2차(26-07-27) §2 — KST 고정. 정본 kstDateTime 은 '2026.07.27 14:30' 한 덩이라
            // 날짜/시각 두 줄로 공백에서 나눠 쓴다.
            const [date = '', time = ''] = (kstDateTime(e.occurredAt) ?? '').split(' ');
            const base = {
              id: e.id,
              date,
              time,
              color: TIMELINE_DOT_COLOR[e.kind] ?? 'var(--fg-muted)',
              title: e.summary,
              tag: TIMELINE_KIND_LABEL[e.kind] ?? e.kind,
              // 개선 9건 차수2(26-08-04) §3 — '누가'. 기록되지 않은 줄은 서버가 '—' 를 내려준다.
              meta: timelineActorName(e.detail),
            };
            if (!editing) return base;
            if (e.hidden) {
              const hid = e.hidden;
              return {
                ...base,
                muted: true,
                // 되돌려서 숨긴 줄은 위 띠의 [되돌리기 취소] 하나로 함께 돌아온다.
                actions:
                  hid.kind === 'REWIND' ? (
                    <span className="rd-edit-tag">되돌려서 숨김</span>
                  ) : (
                    <>
                      <span className="rd-edit-tag">지운 줄</span>
                      <Button
                        type="button"
                        variant="secondary"
                        style={smallBtn}
                        disabled={busy}
                        onClick={() => revertM.mutate({ id: hid.editId, kind: hid.kind })}
                      >
                        다시 살리기
                      </Button>
                    </>
                  ),
              };
            }
            const laterCount = live.findIndex((x) => x.id === e.id);
            const isNewest = e.id === newestId;
            return {
              ...base,
              onPick: isNewest
                ? undefined
                : () => setDialog({ type: 'rewind', entry: e, laterCount }),
              pickLabel: `${e.summary} 시점으로 되돌리기`,
              actions: (
                <>
                  {latestRewind?.targetEntryId === e.id && (
                    <span className="rd-edit-tag target">여기로 되돌림</span>
                  )}
                  {isNewest ? (
                    <span className="rd-edit-tag">지금 상태</span>
                  ) : (
                    <span className="rd-edit-hint">이 시점으로 되돌리기</span>
                  )}
                  <button
                    type="button"
                    className="rd-edit-hide"
                    aria-label={`${e.summary} 줄만 지우기`}
                    disabled={busy}
                    onClick={() => setDialog({ type: 'hide', entry: e, laterCount: 0 })}
                  >
                    <Icon name="trash" size={14} aria-hidden />
                    지우기
                  </button>
                </>
              ),
            };
          })}
        />
      )}
      {!editing && entries.length > OVERVIEW_TIMELINE_LIMIT && (
        <div className="rd-tl-foot">
          {expanded
            ? null
            : `${entries.length}줄 가운데 ${OVERVIEW_TIMELINE_LIMIT}줄을 보여 줍니다. `}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'var(--fg-brand)',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 550,
            }}
          >
            {expanded ? '접기' : '모두 보기'}
          </button>
        </div>
      )}
      <HistoryEditDialog
        dialog={dialog}
        busy={busy}
        onCancel={() => setDialog(null)}
        onConfirm={(d) => (d.type === 'rewind' ? rewindM : hideM).mutate(d.entry.id)}
      />
    </section>
  );
}

function HistoryEditDialog({
  dialog,
  busy,
  onCancel,
  onConfirm,
}: {
  dialog: HistoryDialog | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (d: HistoryDialog) => void;
}) {
  const d = dialog;
  const when = d ? (kstDateTime(d.entry.occurredAt) ?? '') : '';
  return (
    <Modal
      open={d != null}
      onClose={onCancel}
      title={d?.type === 'hide' ? '이 줄만 지우시겠습니까?' : '해당 시점으로 되돌리시겠습니까?'}
      width={460}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
            취소
          </Button>
          <Button
            type="button"
            // 되돌리기 · 지우기는 편집 모드의 주황 계열로 — 초록(확정) · 빨강(삭제)과 다른 일이다.
            style={{ background: 'var(--fg-on-warning-tint)', color: 'var(--brand-on)' }}
            onClick={() => d && onConfirm(d)}
            disabled={busy}
            data-testid="history-edit-confirm"
          >
            {busy ? '처리 중…' : d?.type === 'hide' ? '지우기' : '되돌리기'}
          </Button>
        </>
      }
    >
      {d && (
        <div className="rd-edit-dlg">
          <div className="rd-edit-dlg-target">
            <span className="rd-mono">{when}</span>
            {d.entry.summary}
          </div>
          {d.type === 'rewind' ? (
            <>
              <p>
                그 뒤의 이력 {d.laterCount}줄이 화면에서 사라지고, 로봇의 상태와 위치, 출고 기록도
                그때 모습으로 돌아갑니다. 그 사이 회수로 생긴 회수 정비 오더는 아직 손대지 않았으면
                함께 취소됩니다.
              </p>
              <p className="rd-edit-dlg-sub">
                사라진 줄은 [편집]에서 되돌리기 취소로 다시 살릴 수 있고, 한 일은 감사 로그에
                남습니다.
              </p>
            </>
          ) : (
            <p>
              이 줄만 이력에서 숨깁니다. 로봇 상태는 바뀌지 않습니다. 같은 일로 함께 남은 줄(배치와
              그때의 상태 변경)이 있으면 같이 숨깁니다.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

const smallBtn: CSSProperties = { height: 28, padding: '0 10px' };

function TabButton({
  tabKey,
  active,
  onClick,
  onKeyDown,
  children,
}: {
  tabKey: Tab;
  active: boolean;
  onClick: () => void;
  onKeyDown?: (e: KeyboardEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      id={`tab-${tabKey}`}
      aria-selected={active}
      aria-controls={`panel-${tabKey}`}
      tabIndex={active ? 0 : -1}
      onClick={onClick}
      onKeyDown={onKeyDown}
      className="rd-tab"
    >
      {children}
    </button>
  );
}

// 개요 — 지금 장착 로더 요약. 상세(거쳐 간 로봇 · 교체)는 '로더' 탭.
// 로더 탭과 같은 query key 를 써서 react-query 가 요청을 dedupe 한다.
function CurrentLoaderFacts({ robotId }: { robotId: string }) {
  const q = useQuery({
    queryKey: ['robot-loader', robotId],
    queryFn: () => robotLoaderApi.get(robotId),
    staleTime: 30_000,
  });
  const loader = q.data?.current ?? null;
  return (
    <FactSection title="현재 로더">
      {q.isLoading ? (
        <p className="kuun-caption">불러오는 중…</p>
      ) : loader ? (
        <FactList>
          <Fact
            k="시리얼"
            v={
              <Link to={`/manufacturing/loaders/${loader.id}`} style={{ color: 'var(--fg-brand)' }}>
                {loader.serial}
              </Link>
            }
            mono
          />
          <Fact k="구분" v={formatLoaderSerial(loader.serial)} />
          {/* 구성코드 — 올리면 코드별로 어떤 개조인지(로더 목록 · 로더 상세와 같은 부품). */}
          {loader.configCode && (
            <Fact k="구성코드" v={<LoaderConfigCode code={loader.configCode} focusable />} />
          )}
          <Fact k="상태" v={LOADER_STATUS_LABELS[loader.status]} />
        </FactList>
      ) : (
        <p style={{ color: 'var(--fg-muted)' }}>
          장착된 로더가 없습니다.{' '}
          <Link to={`/robots/${robotId}?tab=loader`} style={{ color: 'var(--fg-brand)' }}>
            로더 탭
          </Link>
          에서 장착할 수 있습니다.
        </p>
      )}
    </FactSection>
  );
}

function InfoCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      style={{
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--r-card)',
        background: 'var(--bg-surface)',
        boxShadow: 'var(--shadow-sm)',
        padding: 16,
      }}
    >
      <div className="kuun-label" style={{ marginBottom: 10 }}>
        {title}
      </div>
      {children}
    </section>
  );
}
